// src/pages/SPanel.jsx
//
// Pagina principală a elevului: un ecran de widget-uri, ca la iPhone (grila,
// mărimile și modul „Editează” — Home/HomeGrid.jsx, Home/homeLayout.js).
// Aici se încarcă datele: lecțiile (cu sincronizare la 45 s și la revenirea
// în tab), statistica practicii, activitatea pe zile, videoclipurile,
// examenul teoretic. Calendarul săptămânii e pe pagina Calendar.
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";

import { UserContext } from "../UserContext";
import Footer from "../components/Footer";
import StudentMobileTopBar from "../components/Header/StudentMobileTopBar";
import HomeGrid from "../components/SPanel/Home/HomeGrid";
import { renderHomeWidget } from "../components/SPanel/Home/HomeWidgets";
import {
   addWidget,
   availableWidgets,
   loadLayout,
   saveLayout,
} from "../components/SPanel/Home/homeLayout";
import { buildLessonTrack } from "../components/SPanel/Home/homeLessons";
import useStudentHomeData from "../components/SPanel/Home/useStudentHomeData";
import { openPopup } from "../components/Common/popupUIStore";
import { loadStudentCalendarEvents } from "../utils/studentCalendarEvents";

// Polling la 45s când tab-ul e vizibil — preia rezervările noi/modificate de admin/manager
// fără să ceară refresh manual de la elev.
const SPANEL_POLL_INTERVAL_MS = 45_000;
// Nu repornești polling-ul mai des de 8s (evită spam la focus/visibilitychange rapid).
const SPANEL_RESYNC_COOLDOWN_MS = 8_000;

// „acum” se reîmprospătează des, ca numărătoarea inversă să meargă singură
const NOW_TICK_MS = 30_000;

/** Examenul practic de arătat: cel activ, altfel cel mai recent. */
function pickPracticalExam(events) {
   const exams = (events || [])
      .filter((event) => event.kind === "practicalExam")
      .sort((a, b) => b.start - a.start);
   return exams.find((event) => event.cardItem?.current) || exams[0] || null;
}

function SPanel() {
   const { user } = useContext(UserContext);
   const [events, setEvents] = useState([]);
   const [now, setNow] = useState(() => new Date());
   const lastSyncAtRef = useRef(0);
   const pollTimerRef = useRef(0);

   const { practice, notifications, videos, theory, activity } = useStudentHomeData();

   // aranjamentul widget-urilor, salvat pe dispozitiv pentru fiecare cont;
   // null = cel implicit (pagina de dinainte de widget-uri)
   const userId = user?.id ?? null;
   const [savedLayout, setSavedLayout] = useState(() => loadLayout(userId));
   useEffect(() => {
      setSavedLayout(loadLayout(userId));
   }, [userId]);
   const updateLayout = useCallback(
      (next) => {
         setSavedLayout(next);
         saveLayout(userId, next);
      },
      [userId],
   );
   // galeria primește și cum se desenează un widget, pentru previzualizări
   const dataRef = useRef(null);
   const openGallery = useCallback(
      (layout) => {
         openPopup("homeWidgetGallery", {
            available: availableWidgets(layout),
            onAdd: (id, size) => updateLayout(addWidget(layout, id, size)),
            renderPreview: (id, size) => renderHomeWidget({ id, size }, dataRef.current),
         });
      },
      [updateLayout],
   );

   const applyEvents = useCallback((allEvents) => {
      setEvents(allEvents);
      setNow(new Date());
   }, []);

   useEffect(() => {
      const timer = window.setInterval(() => setNow(new Date()), NOW_TICK_MS);
      return () => window.clearInterval(timer);
   }, []);

   const track = useMemo(() => buildLessonTrack(events, now), [events, now]);
   const practical = useMemo(() => pickPracticalExam(events), [events]);

   const syncEvents = useCallback(async () => {
      try {
         const allEvents = await loadStudentCalendarEvents();
         lastSyncAtRef.current = Date.now();
         applyEvents(allEvents);
      } catch (_) {}
   }, [applyEvents]);

   // Încărcare inițială
   useEffect(() => {
      syncEvents();
   }, [syncEvents]);

   // Polling periodic la 45s când tab-ul e activ
   useEffect(() => {
      const schedulePoll = () => {
         if (pollTimerRef.current) return;
         pollTimerRef.current = window.setInterval(() => {
            if (typeof document !== "undefined" && document.hidden) return;
            syncEvents();
         }, SPANEL_POLL_INTERVAL_MS);
      };
      schedulePoll();
      return () => {
         if (pollTimerRef.current) {
            window.clearInterval(pollTimerRef.current);
            pollTimerRef.current = 0;
         }
      };
   }, [syncEvents]);

   // Re-sync la focus / visibilitychange (tab re-activat după ce era în fundal)
   useEffect(() => {
      const handleVisible = () => {
         if (typeof document !== "undefined" && document.hidden) return;
         const now = Date.now();
         if (now - lastSyncAtRef.current < SPANEL_RESYNC_COOLDOWN_MS) return;
         syncEvents();
      };
      window.addEventListener("focus", handleVisible);
      document.addEventListener("visibilitychange", handleVisible);
      return () => {
         window.removeEventListener("focus", handleVisible);
         document.removeEventListener("visibilitychange", handleVisible);
      };
   }, [syncEvents]);

   const data = {
      user,
      track,
      now,
      unread: notifications.unread,
      notifications,
      practice,
      videos,
      theory,
      practical,
      activity,
   };

   dataRef.current = data;

   return (
      <main className="main">
         <div className="shome">
            {/* pe ecran lat; pe telefon e bara fixă de sus (AppShell) */}
            <StudentMobileTopBar variant="inline" showEdit />
            <HomeGrid
               savedLayout={savedLayout}
               onChange={updateLayout}
               renderWidget={(item) => renderHomeWidget(item, data)}
               onAdd={openGallery}
               onReset={() => updateLayout(null)}
            />
         </div>

         <Footer />
      </main>
   );
}

export default SPanel;
